import React from 'react'
import { Button, Divider, Grid, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useFieldArray, useWatch } from 'react-hook-form'
import GroupCard, { emptyGroup } from './GroupCard'
import { impliedTicketFields } from '../../utils/luckyDraw'

/**
 * The draw's prize groups, in the order the draw screen runs them.
 *
 * The `useWatch` subscription lives here rather than on the page so a keystroke in any prize field
 * re-renders this component alone — GroupCard is memoised and bails out unless its own numbering
 * changed. It watches values only to count each group's prizes, which is what shifts the running
 * numbers; the values themselves are never read.
 */
const GroupList = ({ control, getValues, ticketFieldOptions }) => {
  const { fields, append, remove, swap } = useFieldArray({ control, name: 'groups' })
  const groups = useWatch({ control, name: 'groups' }) ?? []

  /** Where this group's first prize falls across the whole draw. */
  const startNumberOf = (index) =>
    groups.slice(0, index).reduce((total, group) => total + (group.prizes?.length ?? 0), 1)

  return (
    <>
      <Grid item xs={12} sx={{ my: 4 }}>
        <Typography variant='h2' sx={{ fontSize: '1.5rem' }}>
          Prize Groups
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Each group is its own pool. The draw screen runs them in this order.
        </Typography>
      </Grid>

      {fields.map((groupField, index) => (
        <React.Fragment key={groupField.id}>
          {index > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ borderStyle: 'dashed', my: 4 }} />
            </Grid>
          )}
          <GroupCard
            control={control}
            getValues={getValues}
            index={index}
            startNumber={startNumberOf(index)}
            awarded={groupField.awarded ?? 0}
            ticketFieldOptions={ticketFieldOptions}
            isFirst={index === 0}
            isLast={index === fields.length - 1}
            canRemove={fields.length > 1}
            swapGroup={swap}
            removeGroup={remove}
          />
        </React.Fragment>
      ))}

      <Grid item xs={12}>
        <Button
          startIcon={<AddIcon />}
          onClick={() =>
            append({ ...emptyGroup, ticketFields: impliedTicketFields(ticketFieldOptions) })
          }
        >
          Add Group
        </Button>
      </Grid>
    </>
  )
}

export default GroupList
